/**
 * Build SBOM readable HTML from backend readable-view JSON
 * ({ metadata, components }) — layout aligned with customer intent sample.
 */

import {
  VEX_JUSTIFICATION_OPTIONS,
  VEX_STATUS,
  VEX_STATUS_OPTIONS,
} from "@constants/vex";
import { formatDateTime } from "@utils/common";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

const ATTRIBUTE_FIELDS = [
  { key: "modification", label: "Modification" },
  { key: "open", label: "Open" },
  { key: "notice", label: "Notice" },
  { key: "patent", label: "Patent" },
];

const CSS = `
* { box-sizing: border-box; }

body {
  font-family: Arial, sans-serif;
  margin: 40px;
  line-height: 1.6;
  color: #333;
  scroll-behavior: smooth;
  font-size: 13px;
  background: #fff;
  --sbom-accent: #3498db;
  --sbom-accent-hover: #2980b9;
}

h1 {
  color: #2c3e50;
  padding-bottom: 10px;
  margin-bottom: 4px;
}

.notice-header {
  color: #6b7280;
  font-weight: 600;
  border-bottom: 2px solid #3498db;
  padding-bottom: 10px;
  margin: 0 0 16px 0;
}

.summary-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 22px;
  margin: 10px 0 4px 0;
  padding: 0;
  list-style: none;
}

.summary-list li { color: #495057; }
.summary-list strong { color: #2c3e50; }

.list-controls {
  margin: 20px 0 10px 0;
}

.toggle-btn {
  padding: 8px 14px;
  background-color: var(--sbom-accent);
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}

.toggle-btn:hover {
  background-color: var(--sbom-accent-hover);
}

.page-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 8px;
  background: transparent;
  color: var(--sbom-accent);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
}

.page-btn:hover:not(:disabled) {
  color: var(--sbom-accent-hover);
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-btn svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.page-number {
  min-width: 36px;
  padding: 4px 12px;
  background: #fff;
  color: #2c3e50;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}

.page-number:hover:not(.is-active) {
  background: #f8fafc;
}

.page-number.is-active {
  background: var(--sbom-accent);
  color: #fff;
  border-color: var(--sbom-accent);
  cursor: default;
}

.page-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  padding: 4px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #fff;
  color: #2c3e50;
  font-size: 13px;
  user-select: none;
}

.page-numbers {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.pagination-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  color: #495057;
}

.page-size-field {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.page-size-dropdown {
  position: relative;
  display: inline-block;
}

.page-size-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 64px;
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #fff;
  color: #2c3e50;
  font-size: 13px;
  cursor: pointer;
}

.page-size-trigger:hover,
.page-size-dropdown.is-open .page-size-trigger {
  border-color: var(--sbom-accent);
}

.page-size-trigger:focus {
  outline: none;
  border-color: var(--sbom-accent);
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.25);
}

.page-size-trigger-icon {
  width: 12px;
  height: 12px;
  color: #64748b;
  flex-shrink: 0;
}

.page-size-menu {
  display: none;
  position: absolute;
  left: 0;
  bottom: calc(100% + 4px);
  z-index: 20;
  min-width: 100%;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
}

.page-size-dropdown.is-open .page-size-menu {
  display: block;
}

.page-size-option {
  display: block;
  width: 100%;
  padding: 6px 10px;
  border: none;
  background: transparent;
  color: #2c3e50;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.page-size-option:hover {
  background: #f1f5f9;
}

.page-size-option.is-selected {
  background: var(--sbom-accent);
  color: #fff;
}

.page-size-option.is-selected:hover {
  background: var(--sbom-accent-hover);
  color: #fff;
}

.component {
  margin: 14px 0;
  border: 1px solid #A1A7B0;
  border-radius: 5px;
  background-color: #ECF0F1;
  overflow: hidden;
}

.component > summary {
  cursor: pointer;
  font-weight: 700;
  font-size: 13px;
  color: #2c3e50;
  outline: none;
  padding: 11px 12px;
  display: grid;
  grid-template-columns: minmax(160px, 280px) minmax(120px, 200px) 150px;
  justify-content: start;
  align-items: center;
  column-gap: 16px;
  list-style: none;
}

.component > summary::-webkit-details-marker {
  display: none;
}

.component[open] > summary {
  border-bottom: 1px solid #cbd5e1;
}

.summary-name-col {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.component-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  flex-shrink: 0;
  color: #2c3e50;
  font-size: 11px;
  line-height: 1;
}

.component-toggle::before {
  content: "▶";
}

.component[open] > summary .component-toggle::before {
  content: "▼";
}

.component-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.component-summary-meta {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
  font-weight: 400;
  min-width: 0;
}

.component-summary-status {
  justify-self: start;
  text-align: left;
}

.meta {
  color: #6b7280;
  font-style: italic;
}

.license-meta {
  color: #278a53;
}

.badge, .status-pill, .attribute-pill {
  display: inline-block;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  line-height: 18px;
  font-weight: 700;
  white-space: nowrap;
}

.vuln-count {
  background: #fff1f2;
  color: #9f1239;
}

.safe-badge {
  background: #ecfdf5;
  color: #166534;
}

/* Sync with Security Information VexProgressChip / communicationTag* */
.vex-affected {
  background: #FF4D3A26;
  color: #FF4D3A;
  border: 1px solid #FF4D3A;
}

.vex-investigation {
  background: #FFAF011C;
  color: #FFAF01;
  border: 1px solid #FFAF01;
}

.vex-fixed {
  background: #42C83C1C;
  color: #42C83C;
  border: 1px solid #42C83C;
}

.vex-not-affected {
  background: #01BBFE26;
  color: #01BBFE;
  border: 1px solid #01BBFE99;
}

.vex-neutral {
  background: #e5e7eb;
  color: #4b5563;
  border: 1px solid #9ca3af;
}

.vex-status-wrap {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  position: relative;
}

.justification-info {
  width: 17px;
  height: 17px;
  padding: 0;
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  background: #fff;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  line-height: 15px;
  text-align: center;
  cursor: pointer;
  flex: 0 0 17px;
}

.justification-info:hover,
.justification-info:focus {
  background: #f8fafc;
  border-color: #94a3b8;
  outline: none;
}

.justification-popover {
  position: fixed;
  z-index: 40;
  display: none;
  width: 280px;
  max-width: calc(100vw - 24px);
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  color: #334155;
  font-size: 12px;
  line-height: 1.45;
  white-space: normal;
  overflow-wrap: anywhere;
}

.justification-popover.show {
  display: block;
}

.justification-popover-title {
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
}

.component-body {
  background: #fff;
  padding: 14px 16px 16px;
}

.info-list {
  max-width: 760px;
  margin-bottom: 16px;
}

.info-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 14px;
  padding: 5px 0;
}

.info-label {
  color: #2c3e50;
  font-weight: 700;
}

.info-value {
  color: #495057;
  min-width: 0;
}

.attribute-pill {
  background: #eff6ff;
  color: #1d4ed8;
  margin: 0 4px 4px 0;
}

.subsection-title {
  margin-top: 12px;
  margin-bottom: 8px;
  padding-bottom: 6px;
  color: #2c3e50;
  font-weight: 700;
  border-bottom: 2px solid #3498db;
}

.count {
  color: #6b7280;
  font-weight: 400;
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
}

.vulnerability-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}

.vulnerability-table th,
.vulnerability-table td {
  border-bottom: 1px solid #dee2e6;
  padding: 8px 10px;
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;
}

.vulnerability-table th {
  background: #f8fafc;
  color: #2c3e50;
  font-weight: 700;
}

.vulnerability-table a {
  color: #3498db;
  text-decoration: none;
  font-weight: 700;
}

.vulnerability-table a:hover {
  text-decoration: underline;
}

.severity-critical {
  color: #ef4444;
  border-color: #ef4444;
}
.severity-high {
  color: #fbbf24;
  border-color: #fbbf24;
}
.severity-medium {
  color: #42C83C;
  border-color: #42C83C;
}
.severity-low {
  color: #38bdf8;
  border-color: #38bdf8;
}
.severity-unknown {
  color: #9ca3af;
  border-color: #6b7280;
}

.severity-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 4rem;
  padding: 0 6px;
  border: 1px solid;
  border-radius: 4px;
  font-size: 11px;
  line-height: 16px;
  font-weight: 500;
}

.empty-state {
  margin-top: 8px;
  padding: 10px 12px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}

.pagination-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.vuln-section {
  margin-top: 4px;
}

.vuln-pagination {
  margin-top: 12px;
  padding-top: 0;
  border-top: none;
  justify-content: flex-start;
}

.vuln-pagination .pagination-controls {
  gap: 8px;
  font-size: 12px;
}

.vuln-pagination .page-size-field {
  gap: 4px;
  color: #64748b;
}

.vuln-pagination .page-size-trigger {
  min-width: 52px;
  padding: 2px 6px;
  font-size: 12px;
}

.vuln-pagination .page-size-trigger-icon {
  width: 10px;
  height: 10px;
}

.vuln-pagination .page-size-option {
  padding: 4px 8px;
  font-size: 12px;
}

.vuln-pagination .page-btn {
  padding: 4px 6px;
  font-size: 11px;
}

.vuln-pagination .page-btn svg {
  width: 14px;
  height: 14px;
}

.vuln-pagination .page-number,
.vuln-pagination .page-ellipsis {
  min-width: 28px;
  padding: 2px 8px;
  font-size: 12px;
}

@media (max-width: 760px) {
  body { margin: 20px; }
  .info-row { grid-template-columns: 1fr; gap: 2px; }
  .component > summary {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .component-summary-status {
    justify-self: start;
    text-align: left;
  }
}
`;

const PAGE_SCRIPT = `
<script>
(function () {
  function initSbomReadableView() {
    var btn = document.getElementById('toggle-components-btn');
    var pageSizeDropdown = document.getElementById('page-size-dropdown');
    var pageSizeTrigger = document.getElementById('page-size-trigger');
    var pageSizeValue = document.getElementById('page-size-value');
    var pageSizeMenu = document.getElementById('page-size-menu');
    var prevBtn = document.getElementById('prev-page');
    var nextBtn = document.getElementById('next-page');
    var pageNumbers = document.getElementById('page-numbers');
    var components = Array.prototype.slice.call(document.querySelectorAll('details.component'));
    var allOpen = false;
    var currentPage = 1;
    var pageSize = parseInt((pageSizeTrigger && pageSizeTrigger.getAttribute('data-value')) || '${DEFAULT_PAGE_SIZE}', 10);

    function totalPages() {
      return Math.max(1, Math.ceil(components.length / pageSize) || 1);
    }

    function closePageSizeMenu() {
      if (!pageSizeDropdown) return;
      pageSizeDropdown.classList.remove('is-open');
      if (pageSizeTrigger) pageSizeTrigger.setAttribute('aria-expanded', 'false');
    }

    function openPageSizeMenu() {
      if (!pageSizeDropdown) return;
      document.querySelectorAll('.page-size-dropdown.is-open').forEach(function (dropdown) {
        if (dropdown !== pageSizeDropdown) {
          dropdown.classList.remove('is-open');
          var otherTrigger = dropdown.querySelector('.page-size-trigger');
          if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
        }
      });
      pageSizeDropdown.classList.add('is-open');
      if (pageSizeTrigger) pageSizeTrigger.setAttribute('aria-expanded', 'true');
    }

    function setPageSize(nextSize) {
      pageSize = nextSize;
      if (pageSizeTrigger) pageSizeTrigger.setAttribute('data-value', String(nextSize));
      if (pageSizeValue) pageSizeValue.textContent = String(nextSize);
      if (pageSizeMenu) {
        Array.prototype.forEach.call(pageSizeMenu.querySelectorAll('.page-size-option'), function (optionBtn) {
          var selected = parseInt(optionBtn.getAttribute('data-value'), 10) === nextSize;
          optionBtn.classList.toggle('is-selected', selected);
          optionBtn.setAttribute('aria-selected', selected ? 'true' : 'false');
        });
      }
      currentPage = 1;
      closePageSizeMenu();
      applyPagination();
    }

    function renderPageNumbers() {
      if (!pageNumbers) return;
      var total = totalPages();
      var items = buildPageItems(total, currentPage);
      pageNumbers.innerHTML = '';

      items.forEach(function (item) {
        if (item === 'ellipsis') {
          var dots = document.createElement('span');
          dots.className = 'page-ellipsis';
          dots.textContent = '…';
          pageNumbers.appendChild(dots);
          return;
        }

        var pageBtn = document.createElement('button');
        pageBtn.type = 'button';
        pageBtn.className = 'page-number' + (item === currentPage ? ' is-active' : '');
        pageBtn.textContent = String(item);
        pageBtn.setAttribute('aria-label', 'Go to page ' + item);
        if (item === currentPage) {
          pageBtn.setAttribute('aria-current', 'page');
        } else {
          pageBtn.addEventListener('click', function () { goToPage(item); });
        }
        pageNumbers.appendChild(pageBtn);
      });
    }

    function updatePagerState() {
      if (prevBtn) prevBtn.disabled = currentPage <= 1;
      if (nextBtn) nextBtn.disabled = currentPage >= totalPages();
      renderPageNumbers();
    }

    function applyPagination() {
      var start = (currentPage - 1) * pageSize;
      var end = start + pageSize;
      components.forEach(function (item, index) {
        var visible = index >= start && index < end;
        item.style.display = visible ? '' : 'none';
        if (!visible) item.open = false;
      });
      updatePagerState();
      if (btn) {
        btn.textContent = 'Open all components';
        allOpen = false;
      }
    }

    function goToPage(page) {
      currentPage = Math.min(Math.max(1, page), totalPages());
      applyPagination();
    }

    if (btn) {
      btn.addEventListener('click', function () {
        allOpen = !allOpen;
        var start = (currentPage - 1) * pageSize;
        var end = start + pageSize;
        components.forEach(function (item, index) {
          if (index >= start && index < end) item.open = allOpen;
        });
        btn.textContent = allOpen ? 'Close all components' : 'Open all components';
      });
    }

    if (pageSizeTrigger && pageSizeDropdown) {
      pageSizeTrigger.addEventListener('click', function (event) {
        event.stopPropagation();
        if (pageSizeDropdown.classList.contains('is-open')) {
          closePageSizeMenu();
        } else {
          openPageSizeMenu();
        }
      });
    }

    if (pageSizeMenu) {
      Array.prototype.forEach.call(pageSizeMenu.querySelectorAll('.page-size-option'), function (optionBtn) {
        optionBtn.addEventListener('click', function (event) {
          event.stopPropagation();
          var nextSize = parseInt(optionBtn.getAttribute('data-value'), 10) || ${DEFAULT_PAGE_SIZE};
          setPageSize(nextSize);
        });
      });
    }

    document.addEventListener('click', function (event) {
      document.querySelectorAll('.page-size-dropdown.is-open').forEach(function (dropdown) {
        if (!dropdown.contains(event.target)) {
          dropdown.classList.remove('is-open');
          var trigger = dropdown.querySelector('.page-size-trigger');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('.page-size-dropdown.is-open').forEach(function (dropdown) {
        dropdown.classList.remove('is-open');
        var trigger = dropdown.querySelector('.page-size-trigger');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { goToPage(currentPage - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goToPage(currentPage + 1); });

    applyPagination();
    initVulnerabilityPagers();
    initJustificationPopover();
  }

  function buildPageItems(total, current) {
    if (total <= 7) {
      var all = [];
      for (var i = 1; i <= total; i += 1) all.push(i);
      return all;
    }

    var items = [1];
    var start = Math.max(2, current - 1);
    var end = Math.min(total - 1, current + 1);

    if (current <= 3) {
      start = 2;
      end = 4;
    } else if (current >= total - 2) {
      start = total - 3;
      end = total - 1;
    }

    if (start > 2) items.push('ellipsis');
    for (var page = start; page <= end; page += 1) items.push(page);
    if (end < total - 1) items.push('ellipsis');
    items.push(total);
    return items;
  }

  function initVulnerabilityPagers() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-vuln-pager]'), function (section) {
      var pageSizeDropdown = section.querySelector('.page-size-dropdown');
      var pageSizeTrigger = section.querySelector('.page-size-trigger');
      var pageSizeValue = section.querySelector('.page-size-value');
      var pageSizeMenu = section.querySelector('.page-size-menu');
      var prevBtn = section.querySelector('.js-vuln-prev');
      var nextBtn = section.querySelector('.js-vuln-next');
      var pageNumbers = section.querySelector('.js-vuln-page-numbers');
      var rows = Array.prototype.slice.call(section.querySelectorAll('tr.vuln-row'));
      var currentPage = 1;
      var pageSize = parseInt((pageSizeTrigger && pageSizeTrigger.getAttribute('data-value')) || '${DEFAULT_PAGE_SIZE}', 10);

      function totalPages() {
        return Math.max(1, Math.ceil(rows.length / pageSize) || 1);
      }

      function closePageSizeMenu() {
        if (!pageSizeDropdown) return;
        pageSizeDropdown.classList.remove('is-open');
        if (pageSizeTrigger) pageSizeTrigger.setAttribute('aria-expanded', 'false');
      }

      function openPageSizeMenu() {
        if (!pageSizeDropdown) return;
        document.querySelectorAll('.page-size-dropdown.is-open').forEach(function (dropdown) {
          if (dropdown !== pageSizeDropdown) {
            dropdown.classList.remove('is-open');
            var otherTrigger = dropdown.querySelector('.page-size-trigger');
            if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
          }
        });
        pageSizeDropdown.classList.add('is-open');
        if (pageSizeTrigger) pageSizeTrigger.setAttribute('aria-expanded', 'true');
      }

      function renderPageNumbers() {
        if (!pageNumbers) return;
        var total = totalPages();
        var items = buildPageItems(total, currentPage);
        pageNumbers.innerHTML = '';

        items.forEach(function (item) {
          if (item === 'ellipsis') {
            var dots = document.createElement('span');
            dots.className = 'page-ellipsis';
            dots.textContent = '…';
            pageNumbers.appendChild(dots);
            return;
          }

          var pageBtn = document.createElement('button');
          pageBtn.type = 'button';
          pageBtn.className = 'page-number' + (item === currentPage ? ' is-active' : '');
          pageBtn.textContent = String(item);
          pageBtn.setAttribute('aria-label', 'Go to vulnerability page ' + item);
          if (item === currentPage) {
            pageBtn.setAttribute('aria-current', 'page');
          } else {
            pageBtn.addEventListener('click', function () { goToPage(item); });
          }
          pageNumbers.appendChild(pageBtn);
        });
      }

      function applyPagination() {
        var start = (currentPage - 1) * pageSize;
        var end = start + pageSize;
        rows.forEach(function (row, index) {
          row.style.display = index >= start && index < end ? '' : 'none';
        });
        if (prevBtn) prevBtn.disabled = currentPage <= 1;
        if (nextBtn) nextBtn.disabled = currentPage >= totalPages();
        renderPageNumbers();
      }

      function goToPage(page) {
        currentPage = Math.min(Math.max(1, page), totalPages());
        applyPagination();
      }

      function setPageSize(nextSize) {
        pageSize = nextSize;
        if (pageSizeTrigger) pageSizeTrigger.setAttribute('data-value', String(nextSize));
        if (pageSizeValue) pageSizeValue.textContent = String(nextSize);
        if (pageSizeMenu) {
          Array.prototype.forEach.call(pageSizeMenu.querySelectorAll('.page-size-option'), function (optionBtn) {
            var selected = parseInt(optionBtn.getAttribute('data-value'), 10) === nextSize;
            optionBtn.classList.toggle('is-selected', selected);
            optionBtn.setAttribute('aria-selected', selected ? 'true' : 'false');
          });
        }
        currentPage = 1;
        closePageSizeMenu();
        applyPagination();
      }

      if (pageSizeTrigger && pageSizeDropdown) {
        pageSizeTrigger.addEventListener('click', function (event) {
          event.stopPropagation();
          if (pageSizeDropdown.classList.contains('is-open')) {
            closePageSizeMenu();
          } else {
            openPageSizeMenu();
          }
        });
      }

      if (pageSizeMenu) {
        Array.prototype.forEach.call(pageSizeMenu.querySelectorAll('.page-size-option'), function (optionBtn) {
          optionBtn.addEventListener('click', function (event) {
            event.stopPropagation();
            var nextSize = parseInt(optionBtn.getAttribute('data-value'), 10) || ${DEFAULT_PAGE_SIZE};
            setPageSize(nextSize);
          });
        });
      }

      if (prevBtn) prevBtn.addEventListener('click', function () { goToPage(currentPage - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { goToPage(currentPage + 1); });

      applyPagination();
    });
  }

  function initJustificationPopover() {
    var pop = document.getElementById('justificationPopover');
    var text = document.getElementById('justificationPopoverText');
    if (!pop || !text) return;

    function hideJustificationPopover() {
      pop.classList.remove('show');
      pop.setAttribute('aria-hidden', 'true');
    }

    function showJustificationPopover(button) {
      var value = button.getAttribute('data-justification') || '';
      if (!value) {
        hideJustificationPopover();
        return;
      }

      text.textContent = value;
      pop.classList.add('show');
      pop.setAttribute('aria-hidden', 'false');

      var rect = button.getBoundingClientRect();
      var margin = 8;
      var popWidth = pop.offsetWidth;
      var popHeight = pop.offsetHeight;
      var left = rect.right + 8;
      var top = rect.top - (popHeight / 2) + (rect.height / 2);

      if (left + popWidth > window.innerWidth - margin) {
        left = rect.left - popWidth - 8;
      }

      left = Math.max(margin, Math.min(left, window.innerWidth - popWidth - margin));
      top = Math.max(margin, Math.min(top, window.innerHeight - popHeight - margin));

      pop.style.left = left + 'px';
      pop.style.top = top + 'px';
    }

    document.addEventListener('click', function (event) {
      var btn = event.target.closest('.justification-info');
      if (btn) {
        event.stopPropagation();
        if (pop.classList.contains('show') && text.textContent === btn.getAttribute('data-justification')) {
          hideJustificationPopover();
        } else {
          showJustificationPopover(btn);
        }
        return;
      }
      if (!event.target.closest('#justificationPopover')) {
        hideJustificationPopover();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') hideJustificationPopover();
    });

    window.addEventListener('resize', hideJustificationPopover);
    window.addEventListener('scroll', hideJustificationPopover, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSbomReadableView);
  } else {
    initSbomReadableView();
  }
})();
</script>
`;

const STATUS_LABEL_BY_CODE = Object.fromEntries(
  VEX_STATUS_OPTIONS.map((option) => [option.value, option.label]),
);

const JUSTIFICATION_LABEL_BY_CODE = Object.fromEntries(
  VEX_JUSTIFICATION_OPTIONS.map((option) => [option.value, option.label]),
);

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatGeneratedAt(value) {
  if (value == null || String(value).trim() === "") return "N/A";
  const formatted = formatDateTime(value);
  return formatted && formatted !== "-" ? formatted : "N/A";
}

function valueOrNa(value) {
  if (value == null || String(value).trim() === "") return "N/A";
  return String(value).trim();
}

function licenseDisplay(value) {
  if (value == null || String(value).trim() === "") return "Not specified";
  return String(value).trim();
}

function isAttributeOn(value) {
  if (value == null) return false;
  const normalized = String(value).trim().toLowerCase();
  return Boolean(
    normalized &&
    !["n", "no", "false", "0", "n/a", "-", "null", "undefined"].includes(
      normalized,
    ),
  );
}

function severityClass(severity) {
  const key = String(severity || "").toLowerCase();
  if (key === "critical") return "severity-critical";
  if (key === "high") return "severity-high";
  if (key === "medium") return "severity-medium";
  if (key === "low") return "severity-low";
  return "severity-unknown";
}

function formatSeverityLabel(severity) {
  if (severity == null || String(severity).trim() === "") return "N/A";
  const text = String(severity).trim();
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

function vulnerabilityCountLabel(count) {
  return count === 1 ? "1 vulnerability" : `${count} vulnerabilities`;
}

function humanizeVexStatus(status) {
  if (status == null || String(status).trim() === "") return "N/A";
  const raw = String(status).trim();
  const upper = raw.toUpperCase();
  if (STATUS_LABEL_BY_CODE[upper]) return STATUS_LABEL_BY_CODE[upper];
  if (Object.values(STATUS_LABEL_BY_CODE).includes(raw)) return raw;
  return raw;
}

function humanizeJustification(value) {
  if (value == null || String(value).trim() === "") return "";
  const raw = String(value).trim();
  const lower = raw.toLowerCase();
  if (JUSTIFICATION_LABEL_BY_CODE[lower]) return JUSTIFICATION_LABEL_BY_CODE[lower];
  if (lower.includes("_")) {
    return lower
      .split("_")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  return raw;
}

function normalizeVexStatusCode(status) {
  return String(status || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

function isNotAffectedStatus(status) {
  const code = normalizeVexStatusCode(status);
  if (code === VEX_STATUS.NOT_AFFECTED) return true;
  return String(status || "")
    .trim()
    .toLowerCase() === "not affected";
}

function vexStatusPillClass(status) {
  const code = normalizeVexStatusCode(status);
  if (code === VEX_STATUS.AFFECTED) return "vex-affected";
  if (code === VEX_STATUS.UNDER_INVESTIGATION) return "vex-investigation";
  if (code === VEX_STATUS.FIXED) return "vex-fixed";
  if (code === VEX_STATUS.NOT_AFFECTED) return "vex-not-affected";

  const key = String(status || "").toLowerCase();
  if (key === "not affected") return "vex-not-affected";
  if (key.includes("affected") && !key.includes("not")) return "vex-affected";
  if (key.includes("fixed")) return "vex-fixed";
  if (key.includes("investigation")) return "vex-investigation";
  return "vex-neutral";
}

function resolveVulnHref(vuln) {
  if (vuln?.sourceUrl) return String(vuln.sourceUrl);
  const id = String(vuln?.id || "").trim();
  if (/^CVE-\d{4}-\d+/i.test(id)) {
    return `https://nvd.nist.gov/vuln/detail/${encodeURIComponent(id)}`;
  }
  return "";
}

function renderInfoRow(label, valueHtml) {
  return `
<div class="info-row">
  <div class="info-label">${escapeHtml(label)}</div>
  <div class="info-value">${valueHtml}</div>
</div>`;
}

function renderAttributePills(component) {
  const pills = ATTRIBUTE_FIELDS.filter(({ key }) =>
    isAttributeOn(component?.[key]),
  )
    .map(
      ({ label }) =>
        `<span class="attribute-pill">${escapeHtml(label)}</span>`,
    )
    .join(" ");
  if (!pills) return "";
  return renderInfoRow("Attributes", pills);
}

function renderVexStatusCell(vuln) {
  const rawStatus = vuln?.vex?.status;
  const vexLabel = humanizeVexStatus(rawStatus);
  const pill = `<span class="status-pill ${vexStatusPillClass(rawStatus)}">${escapeHtml(vexLabel)}</span>`;
  const justification = humanizeJustification(vuln?.vex?.justification);

  if (!isNotAffectedStatus(rawStatus) || !justification) {
    return `<td>${pill}</td>`;
  }

  return `<td><span class="vex-status-wrap">
${pill}
<button
  type="button"
  class="justification-info"
  aria-label="Show VEX justification"
  data-justification="${escapeHtml(justification)}"
>i</button>
</span></td>`;
}

function renderVulnerabilityRow(vuln, includeVex) {
  const id = valueOrNa(vuln?.id);
  const href = resolveVulnHref(vuln);
  const cveCell = href
    ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(id)}</a>`
    : escapeHtml(id);
  const source = escapeHtml(valueOrNa(vuln?.sourceName || "N/A"));
  const score = escapeHtml(valueOrNa(vuln?.score));
  const severity = formatSeverityLabel(vuln?.severity);
  const sevClass = severityClass(vuln?.severity);
  const method = escapeHtml(valueOrNa(vuln?.method));
  const vexCell = includeVex ? renderVexStatusCell(vuln) : "";

  return `
<tr class="vuln-row">
  <td>${cveCell}</td>
  <td>${source}</td>
  <td>${score}</td>
  <td><span class="severity-badge ${sevClass}">${escapeHtml(severity)}</span></td>
  <td>${method}</td>
  ${vexCell}
</tr>`;
}

function renderVulnerabilitySection(vulns, includeVex, componentIndex) {
  if (!vulns.length) {
    return `<div class="empty-state">No vulnerabilities are listed for this component in the current SBOM.</div>`;
  }

  const vexHeader = includeVex ? "<th>VEX Status</th>" : "";
  const showPager = vulns.length > PAGE_SIZE_OPTIONS[0];
  return `
<div class="vuln-section"${showPager ? ' data-vuln-pager="true"' : ""}>
  <div class="subsection-title">Vulnerabilities <span class="count">(${vulns.length})</span></div>
  <div class="table-wrap">
    <table class="vulnerability-table">
      <thead>
        <tr>
          <th>CVE</th>
          <th>Source</th>
          <th>Score</th>
          <th>Severity</th>
          <th>Method</th>
          ${vexHeader}
        </tr>
      </thead>
      <tbody>
        ${vulns.map((vuln) => renderVulnerabilityRow(vuln, includeVex)).join("\n")}
      </tbody>
    </table>
  </div>
  ${showPager ? `<div class="pagination-footer vuln-pagination">${renderPaginationControls(`vuln-${componentIndex}`, "Vulnerabilities per page")}</div>` : ""}
</div>`;
}

function renderComponent(component, includeVex, componentIndex) {
  const name = valueOrNa(component?.name);
  const version = valueOrNa(component?.version);
  const license = licenseDisplay(component?.license);
  const bomRef = valueOrNa(component?.bomRef);
  const vulns = Array.isArray(component?.vulnerabilities)
    ? component.vulnerabilities
    : [];
  const count = vulns.length;

  const versionMeta =
    version !== "N/A" ? `<span class="meta">v${escapeHtml(version)}</span>` : "";
  const licenseMeta =
    license !== "Not specified"
      ? `<span class="meta license-meta">${escapeHtml(license)}</span>`
      : "";
  const statusBadge =
    count > 0
      ? `<span class="badge vuln-count">${escapeHtml(vulnerabilityCountLabel(count))}</span>`
      : `<span class="badge safe-badge">No vulnerabilities</span>`;

  return `
<details class="component">
  <summary>
    <span class="summary-name-col">
      <span class="component-toggle" aria-hidden="true"></span>
      <span class="component-name">${escapeHtml(name)}</span>
    </span>
    <span class="component-summary-meta">${versionMeta}${licenseMeta}</span>
    <span class="component-summary-status">${statusBadge}</span>
  </summary>
  <div class="component-body">
    <div class="info-list">
      ${renderInfoRow("Version", escapeHtml(version))}
      ${renderInfoRow("License", escapeHtml(license))}
      ${renderInfoRow("BOM Reference", escapeHtml(bomRef))}
      ${renderAttributePills(component)}
    </div>
    ${renderVulnerabilitySection(vulns, includeVex, componentIndex)}
  </div>
</details>`;
}

function renderPaginationControls(idPrefix = "", perPageLabel = "Components per page") {
  const isVulnPager = Boolean(idPrefix);
  const valueAttr = isVulnPager
    ? 'class="page-size-value"'
    : 'id="page-size-value"';
  const dropdownAttr = isVulnPager ? "" : ' id="page-size-dropdown"';
  const triggerAttr = isVulnPager ? "" : ' id="page-size-trigger"';
  const menuAttr = isVulnPager ? "" : ' id="page-size-menu"';
  const prevAttr = isVulnPager
    ? ' class="page-btn js-vuln-prev"'
    : ' class="page-btn" id="prev-page"';
  const nextAttr = isVulnPager
    ? ' class="page-btn js-vuln-next"'
    : ' class="page-btn" id="next-page"';
  const pageNumbersAttr = isVulnPager
    ? ' class="page-numbers js-vuln-page-numbers"'
    : ' id="page-numbers" class="page-numbers"';
  const label = escapeHtml(perPageLabel);

  return `
<div class="pagination-controls">
  <div class="page-size-field">
    <span>${label}</span>
    <div class="page-size-dropdown"${dropdownAttr}>
      <button
        type="button"
        class="page-size-trigger"${triggerAttr}
        data-value="${DEFAULT_PAGE_SIZE}"
        aria-haspopup="listbox"
        aria-expanded="false"
      >
        <span ${valueAttr}>${DEFAULT_PAGE_SIZE}</span>
        <svg class="page-size-trigger-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <ul class="page-size-menu"${menuAttr} role="listbox" aria-label="${label}">
        ${PAGE_SIZE_OPTIONS.map(
    (size) => `
        <li role="none">
          <button
            type="button"
            class="page-size-option${size === DEFAULT_PAGE_SIZE ? " is-selected" : ""}"
            role="option"
            data-value="${size}"
            aria-selected="${size === DEFAULT_PAGE_SIZE ? "true" : "false"}"
          >${size}</button>
        </li>`,
  ).join("")}
      </ul>
    </div>
  </div>
  <button type="button"${prevAttr} title="Previous page">
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
    </svg>
    <span>Prev</span>
  </button>
  <span${pageNumbersAttr} aria-label="Pagination"></span>
  <button type="button"${nextAttr} title="Next page">
    <span>Next</span>
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
    </svg>
  </button>
</div>`;
}

/**
 * @param {{ metadata?: object, components?: array }} view
 * @returns {string} full HTML document
 */
export function buildSbomReadableHtml(view) {
  const metadata = view?.metadata || {};
  const components = Array.isArray(view?.components) ? view.components : [];
  const includeVex =
    metadata.includeVex === true || metadata.includeVex === "Y";
  const titleName = valueOrNa(metadata.title);
  const pageTitle = `SBOM | ${titleName === "N/A" ? "" : titleName}`.trim();

  const componentCount =
    metadata.componentCount != null ? metadata.componentCount : components.length;
  const vulnerabilityCount =
    metadata.vulnerabilityCount != null
      ? metadata.vulnerabilityCount
      : components.reduce(
        (sum, component) =>
          sum +
          (Array.isArray(component?.vulnerabilities)
            ? component.vulnerabilities.length
            : 0),
        0,
      );

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${escapeHtml(pageTitle)}</title>
<style>${CSS}</style>
</head>
<body>
<h1>${escapeHtml(pageTitle)}</h1>
<p class="notice-header"></p>
<ul class="summary-list">
  <li><strong>Generated at:</strong> ${escapeHtml(formatGeneratedAt(metadata.generatedAt))}</li>
  <li><strong>Author:</strong> ${escapeHtml(valueOrNa(metadata.author))}</li>
  <li><strong>Components:</strong> ${escapeHtml(String(componentCount))}</li>
  <li><strong>Vulnerabilities:</strong> ${escapeHtml(String(vulnerabilityCount))}</li>
  <li><strong>VEX:</strong> ${includeVex ? "Included" : "Not included"}</li>
</ul>
<div class="list-controls">
  <button id="toggle-components-btn" type="button" class="toggle-btn">Open all components</button>
</div>
${components.map((component, index) => renderComponent(component, includeVex, index)).join("\n")}
<div class="pagination-footer">
  ${renderPaginationControls("", "Components per page")}
</div>
<div class="justification-popover" id="justificationPopover" role="tooltip" aria-hidden="true">
  <div class="justification-popover-title">Justification</div>
  <div id="justificationPopoverText"></div>
</div>
${PAGE_SCRIPT}
</body>
</html>`;
}
